/**
 * Image Picker Module
 *
 * Handles image selection from gallery or camera with resizing and compression.
 */

import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';

import { isWeb } from '../platform';

import { blobToBase64 } from './uploader';
import { t } from '../../i18n';

/**
 * Image mode constants for pick source selection
 */
export const imageMode = {
  gallery: 0,
  camera: 1,
  cancel: 2,
};

/**
 * Pick and process images from gallery or camera
 * @param {number} mode - The image source mode (gallery, camera, cancel)
 * @param {Function} setImageLoading - State setter for loading indicator
 * @param {Function} setImagesToSend - State setter for images array
 */
export async function pickImage(mode, setImageLoading, setImagesToSend) {
  console.log('mode: ', mode);
  if (mode == imageMode.cancel) {
    return;
  }

  // result.canceled never gets set on the web version.
  if (!isWeb()) {
    setImageLoading(true);
  }

  // No permissions request is necessary for launching the image library
  const imageOptions = {
    mediaTypes: 'images',
    allowsMultipleSelection: true,
  };

  let result;
  let permission;
  if (mode === imageMode.camera) {
    console.log('requesting permission');
    permission = await ImagePicker.requestCameraPermissionsAsync();
    console.log('PERMISSION: ', permission?.granted, permission);

    // Open gallery if permission to use camera is not granted
    if (!permission.granted) {
      mode = imageMode.gallery;
    }
  }

  result =
    mode === imageMode.camera
      ? await ImagePicker.launchCameraAsync(imageOptions)
      : await ImagePicker.launchImageLibraryAsync(imageOptions);

  // There will be a 2 second delay, but otherwise it will work.
  if (isWeb()) {
    setImageLoading(true);
  }

  let imageTooLarge = false;
  let notAnImage = false;

  if (!result.canceled) {
    let images = [];

    for (let index in result.assets) {
      let image = result.assets[index];

      // If the width can't be calculated, the image file is not valid. It is likely not an image.
      if (!image.width) {
        notAnImage = true;
        continue;
      }

      // Create a manipulation context with the image URI
      const context = ImageManipulator.ImageManipulator.manipulate(image.uri);

      // Apply transformations - resize to 800 pixels width
      context.resize({ width: 800 });

      // Attempt to render the image
      const resizedImageRef = await context.renderAsync();
      const resizedImage = await resizedImageRef.saveAsync({
        base64: true,
        compress: 0.2,
        format: ImageManipulator.SaveFormat.JPEG,
      });

      let sizeInBytes;
      let base64;
      const response = await fetch(resizedImage.uri);
      const blob = await response.blob();
      sizeInBytes = blob.size;
      base64 = await blobToBase64(blob);

      console.log('SIZE: ', sizeInBytes);

      if (sizeInBytes >= 6291456) {
        // 6MB limit
        imageTooLarge = true;
        continue;
      }

      // Add the resized image URI to the images array
      images.push(base64);
    }

    if (imageTooLarge) {
      alert(t('errors:images.tooLarge'));
    }
    if (notAnImage) {
      alert(t('errors:images.notAnImage'));
    }

    setImagesToSend((i) => i.concat(images));
  }
  setImageLoading(false);
}
